import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './EmployerPortalCandidates.module.css';
import employersJobStyles from '../EmployerPortalJobs/EmployerPortalJobs.module.css';
import Yup from '../../../../utils/yupExtensions';
import { City } from 'country-state-city';
import { filterUI } from './FilterUI';
import { useFormik } from 'formik';
import { deleteApplication, deleteCandidates, getEmployerApplicationsList, getJobsList } from '../../../../services/jobService';
import { TableHeaderUI } from './TableHeader';
import { t } from 'i18next';
import { getFavoriteTalents, getJobSeekerList, getTalentsList, removeFromFavoriteList } from '../../../../services/TalentsService';
import { useGetEducationLevelsHook, useGetSkillsHook } from '../../../../utils/utilityHooks';
import GlobalModal from '../../Popups/GlobalModal';
import BubbleButton from '../../Buttons/BubbleButton/BubbleButton';
import CustomButton from '../CustomButton/CustomButton';
import FormSelectField from '../../UpdatedFormUI/FormSelectField/FormSelectField';
import Select from 'react-select';

function EmployerPortalCandidates() {
    const [page] = useState(1);
    const [jobs, setJobs] = useState([]);
    const [cities, setCities] = useState([]);
    const [activeButton, setActiveButton] = useState('');
    const [selectAll, setSelectAll] = useState(false);
    const [selectedCandidates, setSelectedCandidates] = useState([]);
    const [isHovered, setIsHovered] = useState(-1);
    const [candidates, setCandidates] = useState([]);
    const [filteredCandidates, setfilteredCandidates] = useState([]);
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
    const [sortBy, setSortBy] = useState(t("defaultSort"));
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedBulkAction, setSelectedBulkAction] = useState(null);
    const [selectedListDropdown, setSelectedListDropdown] = useState(null);
    const user = useSelector(state => state.user.user);
    const userToken = useSelector(state => state.user.token);
    const features = useSelector(state => state.user.features);
    const educationLevels = useGetEducationLevelsHook();
    let skills = useGetSkillsHook();
    const location = useLocation();
    const navigate = useNavigate();

    // Get the list parameters from URL
    const queryParams = new URLSearchParams(location.search);
    const selectedListName = queryParams.get('list');
    const selectedListId = queryParams.get('listId');
    const [listOptions, setListOptions] = useState([]);
    const [showFilters, setShowFilters] = useState(true);

    const hasDBAccess = features && features.jobseekersDBAccess === 1;

    const formik = useFormik({
        initialValues: {
            title: '',
            city: '',
            country: '',
            list: '',
            nationality: "",
            education_level: "",
            skills: [],
            yearOfExp: ``,
            language: "",
            salary_range: "",
            gender: "",
        },
        validationSchema: Yup.object({}),
        onSubmit: async (values) => { },
    });

    // Fetch favorite lists once
    const fetchFavoriteLists = async () => {
        try {
            const response = await getJobSeekerList(user.id)
            const fetchedLists = response.data.favoriteLists.map((item) => ({
                id: item.id,
                label: item.list_name,
                value: item.list_name,
            }));
            setListOptions(fetchedLists);

            // If we have a selected list name from URL, set it in formik
            if (selectedListName) {
                formik.setFieldValue('list', selectedListName);
                setShowFilters(false); // Hide filters when list is selected
            } else {
                setShowFilters(true);
            }
        } catch (error) {
            console.error("Error fetching favorite lists:", error);
        }
    };

    useEffect(() => {
        if (user && user.id) {
            fetchFavoriteLists();
        }
    }, [user.id]);

    // Apply the selected list to formik values when URL param changes
    useEffect(() => {
        if (selectedListName && selectedListId) {
            // Reset all filters

            formik.resetForm();
            // Set the formik value for list
            formik.setFieldValue('list', selectedListName);
            setShowFilters(false); // Hide filters when list is selected

            // Set the selected list in the dropdown
            const currentList = listOptions.find(list => list.id === parseInt(selectedListId));
            if (currentList) {
                setSelectedListDropdown(currentList);
            } else if (listOptions.length > 0) {
                // Try to find by name if ID doesn't match
                const listByName = listOptions.find(list => list.value === selectedListName);
                if (listByName) {
                    setSelectedListDropdown(listByName);
                }
            }

            // Give some time for formik to update before triggering search
            setTimeout(() => {
                handleFindCandidates(true);
                setHasSearched(true);
            }, 100);
        } else {
            setShowFilters(true);
            setJobSeekers([]); // Clear results when no list is selected
            setHasSearched(false);
            setSelectedListDropdown(null);
            // Reset all form values
            formik.resetForm();
        }
    }, [selectedListName, selectedListId, listOptions]);

    // Listen for list selection changes from ProfileSideBar component
    useEffect(() => {
        const handleListSelectionChange = (event) => {
            const selectedList = event.detail;
            if (selectedList && selectedList.id) {
                // Find the matching list option
                const matchingList = listOptions.find(list => list.id === selectedList.id);
                if (matchingList) {
                    setSelectedListDropdown(matchingList);
                } else {
                    // Create a new option if not found
                    const newOption = {
                        id: selectedList.id,
                        value: selectedList.value,
                        label: selectedList.label
                    };
                    setSelectedListDropdown(newOption);
                }

                // Reset all filter values except 'list' field
                formik.resetForm();
                formik.setFieldValue('list', selectedList.value);

                // Set loading state
                setIsLoading(true);

                // Fetch data for the selected list
                setTimeout(() => {
                    getFavoriteTalents(user.id, 1, 1000, {
                        jobSeekerList: selectedList.value,
                        listId: selectedList.id
                    })
                        .then(response => {

                            // Process and deduplicate talents
                            const uniqueTalents = [];
                            const talentIds = new Set();

                            response.talents.forEach(talent => {
                                if (!talentIds.has(talent.id)) {
                                    talentIds.add(talent.id);
                                    uniqueTalents.push(talent);
                                }
                            });

                            setJobSeekers(uniqueTalents);
                            setHasSearched(true);
                            setShowFilters(false); // Hide filters when list is selected
                            setIsLoading(false);
                        })
                        .catch(error => {
                            console.error("Error fetching talents for list:", error);
                            setJobSeekers([]);
                            setIsLoading(false);
                        });
                }, 100);
            }
        };

        // Handle list clearing event
        const handleListSelectionCleared = () => {
            setSelectedListDropdown(null);

            setJobSeekers([]);
            setHasSearched(false);
            setShowFilters(true);

            formik.resetForm(); // Reset all form values when clearing the list
        };

        // Add event listeners
        window.addEventListener('listSelectionChanged', handleListSelectionChange);
        window.addEventListener('listSelectionCleared', handleListSelectionCleared);

        // Cleanup function
        return () => {
            window.removeEventListener('listSelectionChanged', handleListSelectionChange);
            window.removeEventListener('listSelectionCleared', handleListSelectionCleared);
        };
    }, [user.id, listOptions]);

    useEffect(() => {
        const cities = City.getCitiesOfCountry(formik.values.country);
        const citiesFormat = cities.map(city => ({
            value: city.name,
            label: city.name
        }));
        setCities(citiesFormat);
    }, [formik.values.country]);

    useEffect(() => {
        getJobsList(page, 1000, null, { employer_id: user.id }, user.jobseeker_status || user.employer_status)
            .then((response) => {
                const jobsFormat = response.jobs.map(job => ({
                    value: job.title,
                    label: job.title
                }));
                setJobs(jobsFormat);
            })
            .catch((error) => {
                console.error('Failed to fetch jobs after retries:', error);
            });
    }, [user]);

    const tableHeader = [
        t("tableHeader.empty"),
        t("tableHeader.name"),
        t("tableHeader.location"),
        t("general.UI.nationality"),
        t("tableHeader.gender"),
        t("job.salary"),
        t("tableHeader.group"),
        t("tableHeader.last_active"),
        t("tableHeader.actions")
    ];

    const [jobSeekers, setJobSeekers] = useState([])
    const [favJobSeeker, setFavJobSeeker] = useState([])
    const handleSelectAll = () => {
        if (!selectAll) {
            const allCandidateNames = jobSeekers.map(candidate => candidate.id);
            setSelectedCandidates(allCandidateNames);
        } else {
            setSelectedCandidates([]);
        }
        setSelectAll(!selectAll);
    };
    const handleCandidateSelection = (name) => {
        if (selectedCandidates.includes(name)) {
            setSelectedCandidates(selectedCandidates.filter(candidate => candidate !== name));
        } else {
            setSelectedCandidates([...selectedCandidates, name]);
        }
    };

    const handleRemoveCandidate = async (candidateId) => {
        try {
            await deleteCandidates(user.id, candidateId);
            const candidatesClone = JSON.parse(JSON.stringify(jobSeekers));
            const jobDetail = jobSeekers
            const selectedJobIndex = jobDetail.findIndex(candidate => candidate.id == candidateId);
            if (selectedJobIndex !== -1) {
                candidatesClone.splice(selectedJobIndex, 1);

                setJobSeekers(candidatesClone);
            }
        } catch (error) {
        }
    };

    useEffect(() => {
        const getCandidateList = async () => {
            try {
                const employer_id = user?.id;
                const token = userToken;
                const page = 1;
                const per_page = 10;
                const filters = {
                    show_good_fit: false,
                    show_possible_fit: false,
                    show_rejected: false
                };

                const applications = await getEmployerApplicationsList(employer_id, token, page, per_page, filters);
                setCandidates(applications);
            } catch (error) {
            }
        };

        getCandidateList();
    }, [user, userToken, page]);

    useEffect(() => {
        setfilteredCandidates(candidates)
    }, [candidates])

    const handleFindCandidates = async (withOutLoader) => {
        //  if (withOutLoader) {
        //     setIsLoading(false);
        // } else {
        //     setIsLoading(true);
        // }

        setIsLoading(true);


        setHasSearched(true);

        const hasData = Object.values(formik.values).some(value => {
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            return value !== '' && value !== null && value !== undefined;
        });

        if (hasData || selectedListName) {
            try {
                let filteredCandidates = [];

                // Handle list filtering first
                 if (selectedListName && selectedListId) {
                    try {
                        // Use the specific list ID for filtering
                        const response = await getFavoriteTalents(user.id, 1, 1000, {
                            jobSeekerList: selectedListName,
                            listId: selectedListId
                        });

                        // Deduplicate talents to avoid duplicates in the filtered results
                        const uniqueTalents = [];
                        const talentIds = new Set();

                        response.talents.forEach(talent => {
                            if (!talentIds.has(talent.id)) {
                                talentIds.add(talent.id);
                                uniqueTalents.push(talent);
                            } else {
                                // We've already added this talent, find the existing entry and update list info
                                const existingTalent = uniqueTalents.find(t => t.id === talent.id);

                                // If the talent has list_id or list_name, store it
                                if (talent.list_id || talent.list_name) {
                                    // Initialize the lists array if it doesn't exist
                                    if (!existingTalent.lists) {
                                        existingTalent.lists = [];
                                    }

                                    // Add the list info to the lists array
                                    existingTalent.lists.push({
                                        list_id: talent.list_id,
                                        list_name: talent.list_name
                                    });
                                }
                            }
                        });

                        filteredCandidates = uniqueTalents;
                    } catch (error) {
                        filteredCandidates = [];
                    }
                } else {
                    // If not filtering by list, use allJobSeekers but ensure we have a clean copy to work with
                    filteredCandidates = [...allJobSeekers];

                    // Deduplicate by ID to avoid duplicates in the search results
                    const uniqueIds = new Set();
                    filteredCandidates = filteredCandidates.filter(candidate => {
                        if (uniqueIds.has(candidate.id)) {
                            return false;
                        }
                        uniqueIds.add(candidate.id);
                        return true;
                    });
                }

                // Apply other filters if not filtering by list or if additional filters are applied with list
                if (!selectedListName || (selectedListName && hasData)) {
                    if (formik.values.country) {
                        filteredCandidates = filteredCandidates.filter(
                            (e) => e?.country?.toLowerCase() === formik.values.country.toLowerCase()
                        );
                    }
                    if (formik.values.city) {
                        filteredCandidates = filteredCandidates.filter(
                            (e) => e?.city?.toLowerCase() === formik.values.city.toLowerCase()
                        );
                    }
                    if (formik.values.title) {
                        filteredCandidates = filteredCandidates.filter(
                            (e) => e?.working_title?.toLowerCase().includes(formik.values.title.toLowerCase())
                        );
                    }
                    if (formik.values.nationality) {
                        filteredCandidates = filteredCandidates.filter(
                            (e) => e?.nationality?.toLowerCase() === formik.values.nationality.toLowerCase()
                        );
                    }
                    if (formik.values.education_level) {
                        filteredCandidates = filteredCandidates.filter((e) =>
                            e?.education?.some((item) => item.education_level.toLowerCase() === formik.values.education_level.toLowerCase())
                        );
                    }
                    const filterSkillFormat = skills.filter((e) => e.value == formik.values.skills)[0]
                    if (formik.values.skills && formik.values.skills.length > 0) {
                        filteredCandidates = filteredCandidates.filter((e) =>
                            e?.skills.some((item) => item.skill_code == filterSkillFormat.id)
                        );
                    }
                    if (formik.values.yearOfExp) {
                        const [start, end] = formik.values.yearOfExp.split('-').map(Number);
                        filteredCandidates = filteredCandidates.filter((e) => {
                            const yearsOfExperience = e?.years_of_experience;
                            return yearsOfExperience >= start && yearsOfExperience <= end;
                        });
                    }
                    if (formik.values.language) {
                        filteredCandidates = filteredCandidates.filter(
                            (e) => e?.language?.toLowerCase() === formik.values.language.toLowerCase()
                        );
                    }
                    if (formik.values.salary_range) {
                        const [minSalary, maxSalary] = formik.values.salary_range.split('-').map(Number);
                        filteredCandidates = filteredCandidates.filter((e) => {
                            const basePay = e?.jobPreferences[0]?.basePay;
                            return basePay >= minSalary && basePay <= maxSalary;
                        });
                    }
                    if (formik.values.gender) {
                        filteredCandidates = filteredCandidates.filter(
                            (e) => e?.gender?.toLowerCase() === formik.values.gender.toLowerCase()
                        );
                    }
                }

                setJobSeekers(filteredCandidates);
            } catch (error) {
                console.error("Error filtering candidates:", error);

                setJobSeekers([]);
            }
        } else {

            setJobSeekers([]);
        }
        setIsLoading(false);
    };

    // Function to refresh jobSeekers data
    const refreshJobSeekers = async (withLoading) => {
        try {
            if (withLoading) {
                // If withLoading is true, we do a full refresh with loading UI
                setIsLoading(true);

                // First, make sure we have the latest list options
                await fetchFavoriteLists();
            } else {
                // If withLoading is false, we just update in the background
                // Only update the cell loading indicator, not the full page
                // Don't set isLoading to true to avoid resetting the filters and UI
            }

            const response = await getFavoriteTalents(user.id, 1, 1000, {});

            // Deduplicate talents based on id to avoid duplicate rows
            const uniqueTalents = [];
            const talentIds = new Set();

            // Process the talents to merge list information for the same talent
            response.talents.forEach(talent => {
                if (!talentIds.has(talent.id)) {
                    // This is the first time we've seen this talent
                    talentIds.add(talent.id);
                    // Create a copy with an empty lists array
                    const talentCopy = { ...talent, lists: [] };

                    // If the talent has list_id or list_name, add it to lists array
                    if (talent.list_id || talent.list_name) {
                        talentCopy.lists.push({
                            list_id: talent.list_id,
                            list_name: talent.list_name
                        });
                    }

                    uniqueTalents.push(talentCopy);
                } else {
                    // We've already added this talent, find the existing entry and update list info
                    const existingTalent = uniqueTalents.find(t => t.id === talent.id);

                    // If the talent has list_id or list_name, store it
                    if (talent.list_id || talent.list_name) {
                        // Add the list info to the lists array if it doesn't already exist
                        const listExists = existingTalent.lists.some(
                            list => list.list_id === talent.list_id && list.list_name === talent.list_name
                        );

                        if (!listExists) {
                            existingTalent.lists.push({
                                list_id: talent.list_id,
                                list_name: talent.list_name
                            });
                        }
                    }
                }
            });

            // Now match list_ids with list names from listOptions
            uniqueTalents.forEach(talent => {
                if (talent.lists) {
                    talent.lists.forEach(list => {
                        // If we have list_id but no list_name, try to find the name from listOptions
                        if (list.list_id && !list.list_name) {
                            const listOption = listOptions.find(opt => opt.id === list.list_id);
                            if (listOption) {
                                list.list_name = listOption.label;
                            }
                        }
                    });
                }
            });

            // Update favJobSeeker with new list information
            setFavJobSeeker(uniqueTalents);

            if (withLoading) {
                setIsLoading(false);

                // If a list is selected via URL params, filter the candidates
                if (selectedListName) {
                    handleFindCandidates();
                }

            } else {
                // If we're doing a background refresh, don't reset the filter UI
                // Check if we have displayed candidates
                if (jobSeekers && jobSeekers.length > 0) {
                    // Update existing jobSeekers with new list information from uniqueTalents
                    const updatedJobSeekers = jobSeekers.map(jobSeeker => {
                        // Find the corresponding talent in uniqueTalents
                        const updatedTalent = uniqueTalents.find(talent => talent.id === jobSeeker.id);
                        if (updatedTalent) {
                            // Return the job seeker with updated lists information
                            return {
                                ...jobSeeker,
                                lists: updatedTalent.lists,
                                list_id: updatedTalent.list_id,
                                list_name: updatedTalent.list_name
                            };
                        }
                        // If not found, return the original job seeker
                        return jobSeeker;
                    });

                    // Update jobSeekers with the updated list but preserve other data
                    setJobSeekers(updatedJobSeekers);
                    if (selectedListName) {
                        setTimeout(() => {
                            handleFindCandidates();
                        }, 0);
                    }
                }
            }
        } catch (error) {
            if (withLoading) {
                setIsLoading(false);
            }
        }
    };

    // Make a single API call to load favorite talents
    const loadFavoriteTalents = async () => {
        setIsLoading(true);
        try {
            // First, make sure we have the latest list options
            await fetchFavoriteLists();

            // Then get the favorite talents
            const response = await getFavoriteTalents(user.id, 1, 1000, {});

            // Deduplicate talents based on id to avoid duplicate rows
            const uniqueTalents = [];
            const talentIds = new Set();

            // First pass: Create basic talent objects and collect all talents by ID
            response.talents.forEach(talent => {
                if (!talentIds.has(talent.id)) {
                    // This is the first time we've seen this talent
                    talentIds.add(talent.id);
                    // Create a copy with an empty lists array
                    const talentCopy = { ...talent, lists: [] };

                    // If the talent has list_id or list_name, add it to lists array
                    if (talent.list_id || talent.list_name) {
                        talentCopy.lists.push({
                            list_id: talent.list_id,
                            list_name: talent.list_name
                        });
                    }

                    uniqueTalents.push(talentCopy);
                } else {
                    // We've already added this talent, find the existing entry and update list info
                    const existingTalent = uniqueTalents.find(t => t.id === talent.id);

                    // If the talent has list_id or list_name, store it
                    if (talent.list_id || talent.list_name) {
                        // Add the list info to the lists array if it doesn't already exist
                        const listExists = existingTalent.lists.some(
                            list => list.list_id === talent.list_id && list.list_name === talent.list_name
                        );

                        if (!listExists) {
                            existingTalent.lists.push({
                                list_id: talent.list_id,
                                list_name: talent.list_name
                            });
                        }
                    }
                }
            });

            // Now match list_ids with list names from listOptions
            uniqueTalents.forEach(talent => {
                if (talent.lists) {
                    talent.lists.forEach(list => {
                        // If we have list_id but no list_name, try to find the name from listOptions
                        if (list.list_id && !list.list_name) {
                            const listOption = listOptions.find(opt => opt.id === list.list_id);
                            if (listOption) {
                                list.list_name = listOption.label;
                            }
                        }
                    });
                }
            });

            setJobSeekers([]);
            setFavJobSeeker(uniqueTalents);

            // Also deduplicate allJobSeekers to avoid duplicates in search
            const allUniqueJobSeekers = [];
            const allJobSeekerIds = new Set();

            response.talents.forEach(talent => {
                if (!allJobSeekerIds.has(talent.id)) {
                    allJobSeekerIds.add(talent.id);
                    allUniqueJobSeekers.push(talent);
                }
            });

            // setAllJobSeekers(allUniqueJobSeekers);
            setIsLoading(false);

            // If a list is selected via URL params, filter the candidates
            if (selectedListName) {
                setTimeout(() => {
                    handleFindCandidates();
                }, 500);
            }
        } catch (error) {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (user && user.id) {
            loadFavoriteTalents();
        }
    }, [user.id]);

    const [allJobSeekers, setAllJobSeekers] = useState([]);
    useEffect(() => {
        const MAX_RETRIES = 1000; // Maximum retries
        let retryCount = 0;
        const fetchJobs = (page, per_page, max_pages, filters,) => {
            getTalentsList(page, per_page, max_pages, filters)
                .then((response) => {
                    // Deduplicate talents by ID
                    const uniqueTalents = [];
                    const talentIds = new Set();

                    response.talents.forEach(talent => {
                        if (!talentIds.has(talent.id)) {
                            talentIds.add(talent.id);
                            uniqueTalents.push(talent);
                        }
                    });

                    setAllJobSeekers(uniqueTalents);
                })
                .catch((error) => {
                    if (error.error === 'out of shared memory' && retryCount < MAX_RETRIES) {
                        retryCount++;
                        fetchJobs(1, 1000, 1, {},);
                    } else {
                        console.error('Failed to fetch jobs after retries:', error);
                    }
                });
        }
        fetchJobs(1, 1000, 1, {},)
    }, []);
    useEffect(() => {
        const hasData = Object.values(formik.values).some(value => {
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            return value !== '' && value !== null && value !== undefined;
        });
        if (hasData) { }
        else {
            // setJobSeekers(favJobSeeker)
            if (!selectedListName) {

                // setJobSeekers([]);
                setHasSearched(false);
            }
        }
    }, [formik.values])

    // Function to handle bulk action change
    const handleBulkActionChange = (selectedOption) => {
        setSelectedBulkAction(selectedOption);
    };

    // Function to handle list dropdown change
    const handleListDropdownChange = (selectedOption) => {
        setSelectedListDropdown(selectedOption);
        if (selectedOption) {
            // Update URL parameters
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('listId', selectedOption.id);
            newUrl.searchParams.set('list', encodeURIComponent(selectedOption.value));
            window.history.pushState({ path: newUrl.pathname + newUrl.search }, '', newUrl.href);

            // Reset all filter values

            formik.resetForm();
            // Update formik value for list
            formik.setFieldValue('list', selectedOption.value);

            // Dispatch an event to notify the ProfileSideBar component
            const listSelectedEvent = new CustomEvent('dropdownListSelected', {
                detail: {
                    id: selectedOption.id,
                    list_name: selectedOption.value
                }
            });
            window.dispatchEvent(listSelectedEvent);

            // Set loading state
            setIsLoading(true);

            // Fetch data for the selected list
            setTimeout(() => {
                getFavoriteTalents(user.id, 1, 1000, {
                    jobSeekerList: selectedOption.value,
                    listId: selectedOption.id
                })
                    .then(response => {

                        // Deduplicate talents to avoid duplicates in the filtered results
                        const uniqueTalents = [];
                        const talentIds = new Set();

                        response.talents.forEach(talent => {
                            if (!talentIds.has(talent.id)) {
                                talentIds.add(talent.id);
                                uniqueTalents.push(talent);
                            } else {
                                // We've already added this talent, find the existing entry and update list info
                                const existingTalent = uniqueTalents.find(t => t.id === talent.id);

                                // If the talent has list_id or list_name, store it
                                if (talent.list_id || talent.list_name) {
                                    // Initialize the lists array if it doesn't exist
                                    if (!existingTalent.lists) {
                                        existingTalent.lists = [];
                                    }

                                    // Add the list info to the lists array
                                    existingTalent.lists.push({
                                        list_id: talent.list_id,
                                        list_name: talent.list_name
                                    });
                                }
                            }
                        });

                        setJobSeekers(uniqueTalents);
                        setHasSearched(true);
                        setShowFilters(false); // Hide filters when list is selected
                        setIsLoading(false);
                    })
                    .catch(error => {
                        console.error("Error fetching talents for list:", error);

                        setJobSeekers([]);
                        setIsLoading(false);
                    });
            }, 100);
        } else {
            // Clear list selection

            setJobSeekers([]);
            setHasSearched(false);
            setShowFilters(true);

            // Reset all form values

            formik.resetForm();

            // Dispatch an event to notify the ProfileSideBar component
            const listClearedEvent = new CustomEvent('dropdownListCleared');
            window.dispatchEvent(listClearedEvent);

            // Clear URL parameters
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('listId');
            newUrl.searchParams.delete('list');
            window.history.pushState({ path: newUrl.pathname + newUrl.search }, '', newUrl.href);
        }
    };

    // Function to execute bulk action
    const handleBulkActionSubmit = async () => {
        if (selectedBulkAction?.value === 'remove_from_list' && selectedCandidates.length > 0 && selectedListId) {
            // Skip confirmation modal and directly remove candidates
            await bulkRemoveFromList();
        }
    };

    // Function to remove selected candidates from the list without confirmation
    const bulkRemoveFromList = async () => {
        setIsLoading(true);
        try {
            // Process each selected candidate
            for (const candidateId of selectedCandidates) {
                try {
                    // Make sure we're using the correct list ID from the URL or selected dropdown
                    const listIdToUse = parseInt(selectedListId);

                    // Remove the candidate from the current list using the API
                    await removeFromFavoriteList(user.id, candidateId, listIdToUse);
                } catch (error) {
                    console.error(`Error removing candidate ${candidateId} from list:`, error);
                }
            }

            // Clear selection
            setSelectedCandidates([]);
            setSelectAll(false);

            // Refresh the data for the current list
            if (selectedListId) {
                getFavoriteTalents(user.id, 1, 1000, {
                    jobSeekerList: selectedListName,
                    listId: parseInt(selectedListId)
                })
                    .then(response => {
                        // Process and deduplicate talents
                        const uniqueTalents = [];
                        const talentIds = new Set();

                        response.talents.forEach(talent => {
                            if (!talentIds.has(talent.id)) {
                                talentIds.add(talent.id);
                                uniqueTalents.push(talent);
                            }
                        });

                        setJobSeekers(uniqueTalents);
                        setIsLoading(false);

                        // Dispatch an event to update the counts in the sidebar
                        const listsUpdatedEvent = new CustomEvent('jobseekerListsUpdated', {
                            detail: {
                                userId: user.id
                            }
                        });
                        window.dispatchEvent(listsUpdatedEvent);
                    })
                    .catch(error => {
                        console.error("Error refreshing talents for list:", error);
                        setIsLoading(false);
                    });
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            console.error("Error in bulk action:", error);
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <GlobalModal
                show={showAccessModal}
                onClose={() => setShowAccessModal(false)}
                title={t('general.upgrade.access_required') || 'Database Access Required'}
                message={t('upgrade.jobseekers_db_access_message') || 'You need to upgrade your subscription to access the job seekers database.'}
                primaryButtonText={t('upgrade.upgrade_now') || 'Upgrade Now'}
                secondaryButtonText={t('general.UI.close') || 'Close'}
                navigateTo="/subscriptions"
                secondaryButtonAction={() => setShowAccessModal(false)}
            />

            {hasDBAccess ? (
                <>
                    {showFilters && filterUI(
                        formik,
                        activeButton,
                        setActiveButton,
                        () => { return ("(3)") },
                        jobs,
                        cities,
                        listOptions,
                        handleFindCandidates,
                        false,
                        true,
                        { educationLevels, options: skills },
                        sortBy,
                        setSortBy,
                        showSortDropdown,
                        setShowSortDropdown,
                        false,
                        showAdvanceSearch,
                        setShowAdvanceSearch,
                        isLoading  // Add isLoading as the last parameter
                    )}

                    {/* Show bulk action and list selector when a subtab is active */}
                    {!showFilters && (
                        <div style={{ padding: '20px' }}>
                            <p className={styles.heading}>{t("candidatesFilterUI.candidates")}</p>
                            <div className={styles.bulkActionContainer}>
                                <div className={styles.bulkActionWrapper}>
                                    <Select
                                        placeholder={t('candidatesFilterUI.bulk_action') || "Bulk Action"}
                                        options={[
                                            { value: 'remove_from_list', label: t('candidatesFilterUI.remove_from_list') || "Remove from list" }
                                        ]}
                                        value={selectedBulkAction}
                                        onChange={handleBulkActionChange}
                                        className={styles.bulkActionSelect}
                                        classNamePrefix="bulk-action"
                                        isDisabled={selectedCandidates.length === 0}
                                    />
                                    <CustomButton
                                        dark={!(!selectedBulkAction || selectedCandidates.length === 0)}
                                        style={{
                                            padding: '7px 20px',
                                            marginLeft: '10px',
                                            height: '38px',
                                            opacity: (!selectedBulkAction || selectedCandidates.length === 0) ? 0.5 : 1,
                                            cursor: (!selectedBulkAction || selectedCandidates.length === 0) ? 'not-allowed' : 'pointer'
                                        }}
                                        disabled={!selectedBulkAction || selectedCandidates.length === 0}
                                        onClick={handleBulkActionSubmit}
                                    >
                                        {t("general.UI.submit") || "Submit"}
                                    </CustomButton>
                                </div>

                                <div className={styles.listSelectorWrapper}>
                                    <Select
                                        placeholder={t('candidatesFilterUI.jobseeker_lists') || "Jobseeker Lists"}
                                        options={listOptions}
                                        value={selectedListDropdown}
                                        onChange={handleListDropdownChange}
                                        className={styles.listSelectorSelect}
                                        classNamePrefix="list-selector"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Responsive table container with horizontal scroll */}
                    <div className={styles.tableResponsiveContainer} style={{ boxShadow: 'none' }}>
                        <div className={styles.tableWrapper}>
                            <div className={`${employersJobStyles.row} ${employersJobStyles.tableHeader}`} >
                                {tableHeader.map((item, index) =>
                                    <TableHeaderUI
                                        key={index.toString()}
                                        item={item}
                                        index={index}
                                        tableHeader={tableHeader}
                                        selectAll={selectAll}
                                        handleSelectAll={handleSelectAll}
                                    />
                                )}
                            </div>
                            {isLoading ? (
                                <div className={employersJobStyles.loaderContainer}>
                                    <div className={employersJobStyles.loader}></div>
                                    <p>{t('forms.login.processing')}</p>
                                </div>
                            ) : !hasSearched ? (
                                <p className={styles.noCandidatesText}>{t('candidatesFilterUI.search_to_see_candidates') || 'Use search to find candidates or select a list'}</p>
                            ) : jobSeekers?.length > 0 ? (
                                jobSeekers?.map((item, index) => {
                                    return (
                                        <div
                                            key={index.toString()}
                                            onMouseEnter={() => setIsHovered(index)}
                                            onMouseLeave={() => setIsHovered(-1)}
                                            className={`${employersJobStyles.row} ${employersJobStyles.tableHeader}`}
                                            style={{
                                                height: 'auto',
                                                minHeight: '100px',
                                                border: isHovered === index ? "2px solid #0364b2" : '1px solid #BABBBA',
                                                cursor: "pointer",
                                                position: 'relative',
                                                display: 'flex',
                                                flexWrap: 'nowrap'
                                            }}>
                                            {tableHeader.map((headerItem, headerIndex) =>
                                                <TableHeaderUI
                                                    key={headerIndex.toString()}
                                                    item={item}
                                                    index={headerIndex}
                                                    tableHeader={tableHeader}
                                                    selectAll={selectAll}
                                                    handleSelectAll={handleSelectAll}
                                                    isContent={true}
                                                    isHovered={isHovered}
                                                    setIsHovered={setIsHovered}
                                                    listContent={true}
                                                    isSelected={selectedCandidates?.includes(item?.id)}
                                                    handleCandidateSelection={() => handleCandidateSelection(item?.id)}
                                                    handleRemoveCandidate={() => handleRemoveCandidate(item?.id)}
                                                    listOptions={listOptions}
                                                    refreshJobSeekers={refreshJobSeekers}
                                                    favTalents={favJobSeeker}
                                                />
                                            )}
                                        </div>
                                    )
                                })
                            ) : (
                                <p className={styles.noCandidatesText}>{t('candidatesFilterUI.no_candidates_text')}</p>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <div className={styles.viewCandidatesContainer}>
                    <h2>{t('talents.title') || 'Discover Employees'}</h2>
                    <p>{t('upgrade.jobseekers_db_access_message')}</p>
                    <CustomButton
                        dark
                        onClick={() => setShowAccessModal(true)}
                        style={{ padding: '10px 20px', marginTop: '20px' }}
                    >
                        {t('talents.view_candidates') || 'View Candidates'}
                    </CustomButton>
                </div>
            )}
        </div>
    );
}

export default EmployerPortalCandidates;